import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { FileSignature, FileText, Loader2, Search, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { RecordSuggestionChips } from "@/components/record-suggestion-chips";
import { useGlobalSearchSuggestions } from "@/hooks/use-global-search-suggestions";

// Trimmed shapes — the route only sends the columns the dropdown renders.
type LeadHit = {
  id: string;
  recordId?: string | null;
  leadName?: string | null;
  contactName?: string | null;
  emailAddress?: string | null;
  city?: string | null;
  state?: string | null;
};
type ClientHit = {
  id: string;
  recordId?: string | null;
  accountName?: string | null;
  contactName?: string | null;
  emailAddress?: string | null;
  city?: string | null;
  state?: string | null;
};
type InvoiceHit = {
  id: string;
  recordId?: string | null;
  invoiceNumber?: string | null;
  accountName?: string | null;
  billingContactName?: string | null;
  billingEmail?: string | null;
  status?: string | null;
};

// Locally-typed view of the shared hook's response so the dropdown can
// keep rendering the richer per-row metadata (recordId, emailAddress,
// city, etc.) — the shared hook only types the bare `id` fields used by
// the in-page chip wiring.
type DropdownItems = {
  leads: LeadHit[];
  clients: ClientHit[];
  invoices: InvoiceHit[];
};

/**
 * Sidebar quick-find input. Searches across leads/clients/invoices via
 * /api/search/global and surfaces "Did you mean…?" trigram suggestions
 * whenever the result list is sparse (fewer than 3 hits) — same fuzzy
 * fallback the Knowledge Center already provides for article search,
 * with the threshold raised from zero so a single typo in a multi-word
 * query still earns a suggestion.
 */
export function GlobalQuickFind() {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmedQuery = query.trim();

  const { data, isFetching } = useGlobalSearchSuggestions(query, 250);
  // The shared hook returns minimally-typed buckets (just `id`); cast to
  // the dropdown-specific shape so we can read the richer fields the
  // popover renders (leadName, emailAddress, etc.). The route is the
  // same and already sends those columns — only the type widens.
  const items = data?.items as DropdownItems | undefined;

  // Close the dropdown when the user clicks outside the component. We
  // attach mousedown rather than click so the popover dismisses *before*
  // a downstream click handler can fire (e.g. the user clicks a sidebar
  // nav link — they shouldn't see the dropdown linger over the new page).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Global Cmd/Ctrl+K hotkey to focus the quick-find input. We bail out
  // when the user is already typing in another text field so we don't
  // steal focus away from a form they're filling in. We attach to
  // `document` (bubble phase) and call stopPropagation so the same
  // hotkey doesn't also bubble up to the window-level Command Palette
  // listener — Cmd/Ctrl+K is now owned by the sidebar quick-find.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "k" && e.key !== "K") return;
      if (!(e.metaKey || e.ctrlKey)) return;
      // Cmd/Ctrl+Shift+K (and any other modified combo) is reserved for the
      // Command Palette / other surfaces — let it pass through.
      if (e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        target &&
        (tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable) &&
        target !== inputRef.current
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const suggestions = data?.suggestions ?? [];
  const totalHits =
    (items?.leads.length ?? 0) +
    (items?.clients.length ?? 0) +
    (items?.invoices.length ?? 0);

  const goto = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      data-testid="global-quick-find"
    >
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (trimmedQuery.length > 0) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setQuery("");
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Find lead, client, invoice…"
          aria-label="Search leads, clients, and invoices"
          className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-7 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="input-global-search"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setOpen(false);
            }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover-elevate active-elevate-2"
            aria-label="Clear search"
            data-testid="button-clear-global-search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && trimmedQuery.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
          data-testid="popover-global-search"
        >
          {isFetching && totalHits === 0 ? (
            <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Searching…</span>
            </div>
          ) : totalHits === 0 ? (
            <div className="p-3" data-testid="empty-global-search">
              <p className="text-sm text-muted-foreground" data-testid="text-no-global-results">
                No matches for "{trimmedQuery}".
              </p>
              <RecordSuggestionChips
                suggestions={suggestions}
                onSelect={(s) => setQuery(s)}
                containerTestId="suggestions-global-search"
                buttonTestIdPrefix="button-global-suggestion"
                className="mt-2"
              />
            </div>
          ) : (
            <div className="py-1">
              {items && items.leads.length > 0 && (
                <ResultGroup label="Leads" icon={Users} testId="group-leads">
                  {items.leads.map((l) => (
                    <ResultRow
                      key={l.id}
                      title={l.leadName || l.contactName || "Unnamed lead"}
                      subtitle={[l.contactName, l.emailAddress, [l.city, l.state].filter(Boolean).join(", ")]
                        .filter((p) => p && p !== (l.leadName || ""))
                        .join(" · ")}
                      onClick={() => goto(`/leads?openLead=${encodeURIComponent(l.id)}`)}
                      testId={`result-lead-${l.id}`}
                    />
                  ))}
                </ResultGroup>
              )}
              {items && items.clients.length > 0 && (
                <ResultGroup label="Clients" icon={Users} testId="group-clients">
                  {items.clients.map((c) => (
                    <ResultRow
                      key={c.id}
                      title={c.accountName || c.contactName || "Unnamed client"}
                      subtitle={[c.contactName, c.emailAddress, [c.city, c.state].filter(Boolean).join(", ")]
                        .filter((p) => p && p !== (c.accountName || ""))
                        .join(" · ")}
                      onClick={() => goto(`/clients?openClient=${encodeURIComponent(c.id)}`)}
                      testId={`result-client-${c.id}`}
                    />
                  ))}
                </ResultGroup>
              )}
              {items && items.invoices.length > 0 && (
                <ResultGroup label="Invoices" icon={FileText} testId="group-invoices">
                  {items.invoices.map((i) => (
                    <ResultRow
                      key={i.id}
                      title={
                        i.invoiceNumber
                          ? `Invoice ${i.invoiceNumber}`
                          : i.accountName || "Invoice"
                      }
                      subtitle={[i.accountName, i.billingContactName, i.billingEmail]
                        .filter(Boolean)
                        .join(" · ")}
                      onClick={() => goto(`/invoices?openInvoice=${encodeURIComponent(i.id)}`)}
                      testId={`result-invoice-${i.id}`}
                    />
                  ))}
                </ResultGroup>
              )}
              {totalHits < 3 && (
                <RecordSuggestionChips
                  suggestions={suggestions}
                  onSelect={(s) => setQuery(s)}
                  containerTestId="suggestions-global-search"
                  buttonTestIdPrefix="button-global-suggestion"
                  className="border-t bg-muted/40 px-3 py-2"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({
  label,
  icon: Icon,
  testId,
  children,
}: {
  label: string;
  icon: typeof FileSignature;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b py-1 last:border-b-0" data-testid={testId}>
      <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" />
        <span>{label}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ResultRow({
  title,
  subtitle,
  onClick,
  testId,
}: {
  title: string;
  subtitle?: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-sm",
        "hover-elevate active-elevate-2",
      )}
      data-testid={testId}
    >
      <span className="truncate font-medium">{title}</span>
      {subtitle && (
        <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
      )}
    </button>
  );
}
